//created by sudip
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { fadeInUp } from "@/animations/motion";
import MarketingIllustration from "./MarketingIllustration";

export interface MarketingShowcaseProps {
  eyebrow: string;
  title: string;
  description: string;

  bullets?: string[];

  image: string;
  imageAlt: string;

  accent?: string;

  imagePosition?: "left" | "right";

  primaryCta?: {
    label: string;
    href: string;
  };

  secondaryCta?: {
    label: string;
    href: string;
  };
}

export default function MarketingShowcase({
  eyebrow,
  title,
  description,
  bullets = [],
  image,
  imageAlt,
  accent = "#0F8B8D",
  imagePosition = "right",
  primaryCta,
  secondaryCta,
}: MarketingShowcaseProps) {
  const reverse = imagePosition === "left";

  return (
    <section className="py-12 lg:py-16">
      <div className="mx-auto max-w-7xl px-6">
        <div
          className={cn(
            "grid items-center gap-14 lg:grid-cols-[1fr_1.1fr]",
            reverse &&
              "lg:[&>*:first-child]:order-2 lg:[&>*:last-child]:order-1",
          )}
        >
          {/* Text */}

          <motion.div
            variants={fadeInUp()}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="max-w-xl"
          >
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-primary">
              {eyebrow}
            </p>

            <h2 className="mt-5 font-heading text-4xl font-bold leading-tight lg:text-5xl">
              {title}
            </h2>

            <p className="mt-6 text-lg leading-8 text-muted-foreground">
              {description}
            </p>

            {!!bullets.length && (
              <div className="mt-8 space-y-4">
                {bullets.map((bullet, index) => (
                  <motion.div
                    key={bullet}
                    variants={fadeInUp(0.15 + index * 0.08)}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 className="mt-1 h-5 w-5 flex-shrink-0 text-primary" />

                    <span className="leading-7 text-muted-foreground">
                      {bullet}
                    </span>
                  </motion.div>
                ))}
              </div>
            )}

            {(primaryCta || secondaryCta) && (
              <motion.div
                variants={fadeInUp(0.45)}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="mt-10 flex flex-wrap items-center gap-5"
              >
                {primaryCta && (
                  <Button asChild>
                    <Link to={primaryCta.href}>
                      {primaryCta.label}

                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                )}

                {secondaryCta && (
                  <Link
                    to={secondaryCta.href}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-primary transition-colors hover:text-primary/80"
                  >
                    {secondaryCta.label}

                    <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
              </motion.div>
            )}
          </motion.div>

          {/* Illustration */}

          <MarketingIllustration
            src={image}
            alt={imageAlt}
            accent={accent}
          />
        </div>
      </div>
    </section>
  );
}